import React, { FC, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import CaretBtn from '~src/shared/buttons/caretBtn/CaretBtn';
import SvgIcon, { SvgIconName } from '~src/shared/SvgIcon';
import { alignMiddle } from '~src/styles/utils/positioning';

const DdTriggerContainer = styled.div`
  text-transform: uppercase;
  align-items: center;
  cursor: pointer;
  font-weight: 600;
  text-align: left;
  color: ${({ theme }) => theme.baseBlue.a10};
  display: flex;
  position: relative;

  &.reports-header {
    color: ${({ theme }) => theme.white.a6};
    font-weight: 400;
  }
`;

const TriggerLabel = styled.span<{ $noOverflow?: boolean }>`
  text-overflow: ${({ $noOverflow }) => ($noOverflow ? 'default' : 'ellipsis')};
  overflow: hidden;
  white-space: nowrap;
  text-transform: ${({ $noOverflow }) => ($noOverflow ? 'none' : 'uppercase')};
`;

const IconBox = styled.div`
  margin-right: 5px;
`;

const ResetBtn = styled.button`
  position: absolute;
  right: 0;
  background-color: ${({ theme }) => theme.background};
  ${alignMiddle}

  .iconCrossInCircle {
    color: ${({ theme }) => theme.background};
    fill: ${({ theme }) => theme.baseBlue.a7};
  }

  &:hover .iconCrossInCircle {
    fill: ${({ theme }) => theme.baseBlue.a10};
  }
`;

export interface SelectedOption {
  name?: string;
  nameKey?: string;
}

export interface DdSelectorTriggerProps {
  maxWidth?: string;
  labelKey?: string;
  isDdVisible?: boolean;
  selectedOption?: SelectedOption | SelectedOption[];
  cssClass?: string;
  iconName?: SvgIconName;
  inactive?: boolean;
  inactiveKey?: string;
  blank?: boolean;
  multiSelect?: boolean;
  withReset?: boolean;
  resetSelected?: () => void;
}

const DdSelectorTrigger: FC<DdSelectorTriggerProps> = (props) => {
  const [t] = useTranslation();
  const selectedOption = Array.isArray(props.selectedOption) ? props.selectedOption[0] : props.selectedOption;
  const labelTxt = useMemo(() => {
    const count =
      Array.isArray(props.selectedOption) && props.selectedOption.length > 1
        ? `+${props.selectedOption.length - 1}`
        : '';
    if (selectedOption) {
      return `${
        selectedOption.nameKey ? t(selectedOption.nameKey) : selectedOption.name || '&nbsp;'
      } ${count}`;
    }
  }, [props.selectedOption]);

  const onClickReset = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    if (props.resetSelected) props.resetSelected();
  };

  return (
    <DdTriggerContainer className={`${props.cssClass || ''} ${props.inactive ? 'inactive' : ''}`}>
      {props.iconName && (
        <IconBox>
          <SvgIcon name={props.iconName} />
        </IconBox>
      )}
      <CaretBtn open={props.isDdVisible} size="12px" maxWidth={props.maxWidth}>
        {props.labelKey && <span data-translation={props.labelKey}>{t(props.labelKey)}&nbsp;</span>}
        {!props.blank &&
          (props.inactive || selectedOption ? (
            <TriggerLabel
              data-translation={props.inactive ? props.inactiveKey : selectedOption!.nameKey || 'from BE'}
            >
              {props.inactive && props.inactiveKey ? t(props.inactiveKey) : labelTxt}
            </TriggerLabel>
          ) : (
            <TriggerLabel>&nbsp;-&nbsp;-&nbsp;-&nbsp;</TriggerLabel>
          ))}
      </CaretBtn>
      {props.withReset && !props.isDdVisible && selectedOption && (
        <ResetBtn className="reset-btn" onClick={onClickReset}>
          <SvgIcon name="iconCrossInCircle" width={15} />
        </ResetBtn>
      )}
    </DdTriggerContainer>
  );
};

export default DdSelectorTrigger;
